<script lang="ts">
	import Carousel from "@components/common/Carousel.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { EmblaCarouselType } from "embla-carousel";
	import arrowIcon from "@core/assets/icons/arrow.svg?raw";
	import translations from "./FathersClubAdvantagesSection.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	let canNext = $state(false);
	let canPrev = $state(false);
	let api = $state<EmblaCarouselType>();

	interface Benefit {
		bgClass: string;
		title: string;
		description: string;
		dark: boolean;
	}

	const benefits: Benefit[] = [
		{
			bgClass: "bg-fcramber",
			title: translate("benefit1Title"),
			description: translate("benefit1Description"),
			dark: false,
		},
		{
			bgClass: "bg-fcrtan",
			title: translate("benefit2Title"),
			description: translate("benefit2Description"),
			dark: false,
		},
		{
			bgClass: "bg-fcrslate",
			title: translate("benefit3Title"),
			description: translate("benefit3Description"),
			dark: false,
		},
		{
			bgClass: "bg-fcrterracotta",
			title: translate("benefit9Title"),
			description: translate("benefit9Description"),
			dark: false,
		},
		{
			bgClass: "bg-fcrbrown",
			title: translate("benefit4Title"),
			description: translate("benefit4Description"),
			dark: true,
		},
		{
			bgClass: "bg-fcrblue",
			title: translate("benefit5Title"),
			description: translate("benefit5Description"),
			dark: false,
		},
		{
			bgClass: "bg-fcrsalmon",
			title: translate("benefit6Title"),
			description: translate("benefit6Description"),
			dark: true,
		},
		{
			bgClass: "bg-fcrlinen",
			title: translate("benefit7Title"),
			description: translate("benefit7Description"),
			dark: true,
		},
		{
			bgClass: "bg-fcrperiwinkle",
			title: translate("benefit8Title"),
			description: translate("benefit8Description"),
			dark: true,
		},
	];

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet benefitCard(benefit: Benefit)}
	<article
		class={[
			"w-80 h-40 p-5 flex flex-col items-center justify-center gap-2 text-center shrink-0",
			benefit.bgClass,
			!benefit.dark && "text-white",
		]}
	>
		<Heading level={3} class="text-4/tighter font-antipol font-bold uppercase" text={benefit.title} />
		<p class="text-xs/tighter font-light">
			<Formatted text={benefit.description} />
		</p>
	</article>
{/snippet}

<section class="py-4 md:py-8 lg:py-12 xl:py-16 full-width grid-container bg-fcrbeige">
	<div class="mb-4 text-center uppercase text-fcrorange container-6 font-bold tracking-[.1em]">
		<Formatted text={translate("section")} />
	</div>

	<Heading
		level={2}
		text={translate("title")}
		class="lg:mb-15 font-antipol font-medium text-7/tighter lg:text-8/tighter text-fcrdarkgray sm:max-w-125 text-center sm:mx-auto"
	/>
	<div class={["gap-2 text-fcrdarkgray shrink-0 ml-auto my-9", canNext || canPrev ? "flex lg:hidden" : "hidden"]}>
		<button
			type="button"
			class={[
				"size-16 flex items-center bg-white justify-center border-fcrlightgray-300 disabled:opacity-50 border rounded-full",
				"[&:not(:disabled):hover:active]:scale-95 transition duration-150 ease-in-out",
				"[&:not(:disabled):hover]:border-fcrdarkgray",
			]}
			onclick={(): void => {
				api?.scrollPrev();
			}}
			disabled={!canPrev}
		>
			<Icon icon={arrowIcon} class="size-4.5 rotate-180" />
		</button>
		<button
			type="button"
			class={[
				"size-16 flex items-center bg-white justify-center border-fcrlightgray-300 disabled:opacity-50 border rounded-full",
				"[&:not(:disabled):hover:active]:scale-95 transition duration-150 ease-in-out",
				"[&:not(:disabled):hover]:border-fcrdarkgray",
			]}
			onclick={(): void => {
				api?.scrollNext();
			}}
			disabled={!canNext}
		>
			<Icon icon={arrowIcon} class="size-4.5" />
		</button>
	</div>
	<div class="lg:hidden grid-container full-width">
		<Carousel
			bind:canNext
			bind:canPrev
			bind:api
			items={benefits}
			class="full-width"
			alignWithGridContainer
			itemsContainerClass="gap-4"
			options={{ dragFree: true }}
		>
			{#snippet item(benefit: Benefit)}
				{@render benefitCard(benefit)}
			{/snippet}
		</Carousel>
	</div>
	<div class="hidden lg:flex px-4 justify-center bg-white lg:py-10 xl:py-14 2xl:py-17.5">
		<div class="flex flex-wrap justify-center gap-4.5">
			{#each benefits as benefit (benefit.title)}
				{@render benefitCard(benefit)}
			{/each}
		</div>
	</div>
</section>
